'use client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { JSX, FC } from 'react'
import styled from 'styled-components'
import { motion } from 'motion/react'
import dynamic from 'next/dynamic'
import { useDeviceType, useModal, useTranslator } from '@/hooks'
import { mq, presets, rgbAlpha } from '@/styles'
import type { TypeCmsContentMedium, TypeCmsHomePage } from '@/schema'
import { Img } from './base'
import { MODALS } from '@/context'

const DynamicSlider = dynamic(() => import('./base').then(mod => mod.SliderFlickity), {
  ssr: false,
  loading: () => <div style={{ height: '500px', width: '100%' }} />
})

type Props = {
  data: {
    reels: TypeCmsHomePage['reels']
  }
}

type TypeGetItems = (
  data: TypeCmsContentMedium[],
  type: 'reel' | 'photo',
  setHoveredIndex: (index: number | null) => void,
  onClickReel?: (id: string | number) => void
) => JSX.Element[]

const getItems: TypeGetItems = (data, type, setHoveredIndex, onClickReel) => data.map((item, index) => {
  const { id, medium } = item
  const isVideo = medium.mediumType === 'video_file'
  const key = `${id}-${index}-${type}`
  const loop = true

  return (
    <li key={key}>
      <figure className={'media-wrapper' + ` ${type}${isVideo ? ' video' : ''}`} key={index} onMouseEnter={() => isVideo && setHoveredIndex?.(index)} onMouseLeave={() => isVideo && setHoveredIndex?.(null)}>
        {isVideo ?
          <>
            {(medium.longVideo && id) &&
              <button className='play-button-wrapper' type='button' onClick={(e) => {
                e.stopPropagation()
                onClickReel?.(medium.longVideo)
              }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={'/images/svg/i--play.svg'}  className='icon' draggable={false} alt='Play icon' />
              </button>
            }
            <video className='media' autoPlay muted playsInline loop={loop}>
              <source src={medium.video.src} type='video/mp4' />
            </video>
          </>
          :
          <Img className='media' media={medium.image} />
        }
      </figure>
    </li>
  )
})

const sliderOptions = {
  accessibility: true, // Recommended for accessibility
  autoPlay: false,
  contain: false,
  draggable: true,
  freeScroll: true,
  friction: .1,
  pageDots: false,
  percentPosition: true,
  prevNextButtons: true,
  resize: true, // Recommended for responsiveness
  selectedAttraction: .002,
  setGallerySize: true,
  wrapAround: true,
}

export const SectionTabReels:FC<Props> = ({ data }) => {
  const { reels } = data || { reels: [] }
  const { t } = useTranslator()
  const { updatePayload } = useModal(MODALS.VIDEO)
  const [_hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const { isMobile } = useDeviceType()

  const onClickReel = useCallback((id: string | number) => {
    if(!id) return
    updatePayload({
      open: true,
      payload: {
        data: { id, platform: 'vimeo'}
      }
    })
  }, [updatePayload])

  const ReelsSlide = useMemo(() => getItems(reels, 'reel', setHoveredIndex, onClickReel), [reels, onClickReel])
  const reelsRef = useRef(null)

  const headingId = useMemo(() => t('project.internal_page_links')?.[1]?.id?.substring(1) as string, [t])

  useEffect(() => {
    const flickityInstance = reelsRef.current?.flickityInstance

    const timeout = setTimeout(() => {
      flickityInstance?.resize?.()
      flickityInstance?.reloadCells?.()
    }, 50)

    return () => clearTimeout(timeout)
  }, [])

  return (
    <Section aria-labelledby={headingId}>
      <nav id={headingId} role='tablist'>
        <h2>Reels</h2>
      </nav>
      <div>
        <SliderWrapper ref={reelsRef} role='tabpanel' aria-hidden={false} $active={true} $isMobile={isMobile} initial={false} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} >
          <StyledSliderFlickity className='reels-slider' elementType='ul' options={sliderOptions} dynamicSlider={true} hasCustomDots={false} colorDots='white' customAutoPlay={true} customAutoPlaySpeed={1.5}>
            {ReelsSlide}
          </StyledSliderFlickity>
        </SliderWrapper>
      </div>
			<a className='link--as-button' href='https://vimeo.com/user246690745?fl=pp&fe=sh' target='_blank' rel='noopener noreferrer'>{t('project.see_more')}</a>
    </Section>
  )
}

const Section = styled.section`
  margin-bottom: 75px;
  overflow-x: hidden;
  padding-top: 75px;
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('tablet')} {
    margin-bottom: 110px;
    padding-top: 110px;
  }

  nav {
    align-items: center;
    display: flex;
    gap: 10px;
    grid-column: 1 / span 6;
    justify-content: center;
    margin-bottom: 30px;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      gap: 31px;
      grid-column: 1 / span 12;
    }
  }

  h2 {
    ${presets.font40_60()}
    grid-column: 1 / span 6;
    text-align: center;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
    }
  }

  > div {
    grid-column: 1 / span 6;
    position: relative;
    height: 275px;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      height: 500px;
      grid-column: 1 / span 12;
    }
  }

	.link--as-button {
		align-items: center;
		background-color: var(--color-black);
		border-radius: 100px;
		border: 2px solid var(--color-gold);
		color: var(--color-gold);
		display: flex;
		justify-content: center;
		margin: 20px auto 0;
		padding: 7px 14px;
    position: relative;
		transition: 250ms background-color ease-in-out, 250ms color ease-in-out;
		width: fit-content;
		z-index: 5;

		${mq.greaterThan('tablet')} {
			flex-direction: column;
			grid-column: 11 / span 2;
			padding: 10px 20px;
		}

		&:hover {
			background-color: var(--color-gold);
			color: var(--color-black);
		}
	}
`

const StyledSliderFlickity = styled(DynamicSlider)`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;

  .flickity-slider {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    height: 100%;
  }

  .flickity-viewport {
    height: 100%;
  }
`

const SliderWrapper = styled(motion.div)<{ $active: boolean, $isMobile: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 20px;
  grid-column: 1 / span 6;
  overflow: hidden;
  pointer-events: ${({ $active }) => ($active ? 'auto' : 'none')};
  position: absolute;
  visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
  width: 100%;
  will-change: opacity;

  ${mq.greaterThan('tablet')} {
    grid-column: 1 / span 12;
  }

  div:not(.custom-dots) {
    height:330px;
    width: 100%;
    display: flex;
    align-items: flex-start;

    ${mq.greaterThan('tablet')} {
      height: 550px;
    }
  }

  .play-button-wrapper {
    height: 100%;
    left: 50%;
    pointer-events: auto;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    z-index: 10;

    .icon {
      cursor: pointer;
      height: 52px;
      left: 50%;
      opacity: ${({ $isMobile }) => ($isMobile ? 1 : 0)};
      pointer-events: auto;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      transition: 300ms opacity ease-in-out;
      width: 42px;
      z-index: 100;
    }

    &:hover {
      .icon {
        opacity: 1;
      }
    }
  }

  .reels-slider {
    height:330px;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      height: 500px;
    }
  }

  .custom-dots {
    display: flex;
    flex-direction: row;
    justify-content: center;
    position: absolute;
    bottom: 20px;
    align-items: center;
    left: 50%;
    transform: translateX(-50%);
  }

  > ul {
    display: flex;
    flex-direction: row;
    gap: 20px;
    width: 100%;
  }

  .flickity-slider > .flickity-cell {
    padding-right: 20px !important;
  }

  .media-wrapper {
    align-self: stretch;
    background-color: ${rgbAlpha('--color-white', 0.1)};
    border-radius: 20px;
    flex-shrink: 0;
    height: 275px;
    max-width: 330px;
    overflow: hidden;
    width: fit-content;

    ${mq.greaterThan('tablet')} {
      height: 500px;
      max-width: 400px;
      width: 400px;
    }

    ${mq.greaterThan('desktop-md')} {
      max-width: 400px;
    }
  }

  .reels-slider {
    .media-wrapper {
      ${mq.greaterThan('tablet')} {
        height: 500px;
        max-width: 600px;
        width: 600px;
      }

      ${mq.greaterThan('desktop-md')} {
        max-width: 600px;
      }

      &.video {
        aspect-ratio: 16/9;
        max-width: none;
        width: auto;
      }
    }
  }

  .media {
    position: relative;
    height: 100%;
    object-fit: cover;
    width: 100%;
    z-index: 1;
  }
`
